// Spawn-in (owner 2026-11-03: far camera presets). The director spawns foes 22–40 m from the hero (sim.ts updateSpawns), just
// past the edge of the Default view. The far presets (Far and beyond) show that ring, so a foe would blink into existence
// on screen. Rendering never decides gameplay, so the sim keeps its spawn rules and never reads the camera; instead every
// foe that arrives away from the hero rises out of the ground over SPAWN_IN_T: it grows from a low crouch, climbs out of
// the turf and its glow fades up, with a dust ring on its first frame. A foe that appears close to the hero (splitter
// children, summons, scripted QA crowds) is drawn at once, as before. Render-only: reads Sim enemies, never writes them.

/** seconds a distant spawn takes to rise in */
export const SPAWN_IN_T = 0.55;
/** spawns closer to the hero than this (m) are drawn at once (splits, summons, point-blank QA crowds) */
export const SPAWN_IN_MIN_D = 16;

interface FoeLike { id: number; x: number; z: number; alive: boolean; dummy: number; def: { kind: string } }

export class SpawnIn {
  /** rise-in progress of slot i as of the last frame (1 when unknown) */
  shown(i: number): number { return i < this.last.length ? this.last[i] : 1; }
  private seenId = new Int32Array(0);
  private born = new Float64Array(0);
  /** last k() per enemy slot (the health bars wait until a foe is mostly up) */
  last = new Float32Array(0);
  private owner: unknown = null;
  /** true on the frame a foe was first seen rising (the caller adds the dust ring) */
  fresh = false;

  /** rise-in progress for enemy slot i at sim time `now`: 0 = just arrived, 1 = fully in (and for every foe that needs none) */
  k(sim: unknown, i: number, e: FoeLike, now: number, hx: number, hz: number): number {
    if (sim !== this.owner || this.seenId.length <= i) {
      if (sim !== this.owner) { this.owner = sim; this.seenId = new Int32Array(0); this.born = new Float64Array(0); this.last = new Float32Array(0); }
      const n = Math.max(i + 1, this.seenId.length * 2, 64), id = new Int32Array(n).fill(-1), b = new Float64Array(n).fill(-1e9);
      id.set(this.seenId); b.set(this.born); this.seenId = id; this.born = b;
      const l = new Float32Array(n).fill(1); l.set(this.last.subarray(0, Math.min(this.last.length, n))); this.last = l;
    }
    this.fresh = false;
    if (this.seenId[i] !== e.id) {
      this.seenId[i] = e.id;
      const far = !e.dummy && e.def.kind !== 'boss' && Math.hypot(e.x - hx, e.z - hz) >= SPAWN_IN_MIN_D;
      this.born[i] = far ? now : -1e9;
      this.fresh = far;
    }
    const a = (now - this.born[i]) / SPAWN_IN_T, k = a >= 1 ? 1 : a <= 0 ? 0 : a;
    this.last[i] = k;
    return k;
  }
}

/** eased shape of the rise: scale, sink (m below the ground at 0, times the foe's size) and glow gain */
const _shape = { scale: 1, sink: 0, glow: 1 };
export function spawnInShape(k: number): { scale: number; sink: number; glow: number } {
  const s = k * k * (3 - 2 * k);
  _shape.scale = 0.35 + 0.65 * s; _shape.sink = (1 - s) * 1.4; _shape.glow = 0.25 + 0.75 * k;
  return _shape; // (reused: no per-foe allocation)
}
